import { Asset } from '@graphcommerce/graphcms-ui'
import { ProductPageDescription } from '@graphcommerce/magento-product'
import { Trans } from '@lingui/react'
import { Box, Button, Link } from '@mui/material'
import { useState } from 'react'
import { ProductPage2Query } from '../../../graphql/ProductPage2.gql'
import { CallADruggistQuery } from '../../Dialogs/MoreInformationDialog/CallADruggist.gql'
import { UspsQueryFragment } from '../../Usps/UspsQueryFragment.gql'
import { normalizeHref } from '../../normalizeHref'
import { ProductPageAccordionItem } from './AccordionItem'

type ProductPageAccordionProps = CallADruggistQuery & {
  product: NonNullable<NonNullable<NonNullable<ProductPage2Query['products']>['items']>[0]>
  keurmerkUsps?: UspsQueryFragment['keurmerkUsps']
}

export function ProductPageAccordion(props: ProductPageAccordionProps) {
  const { product, keurmerkUsps, callADruggist } = props
  const { mos_rvh_code, mos_rvg_code } = product
  const isMedical = callADruggist && (mos_rvh_code || mos_rvg_code)
  const keurmerkLogo = keurmerkUsps?.uspsMultiple.find(
    (usp) => usp.identity === 'keurmerk-zelfzorg-online',
  )
  const OnlineMedicationProvidersLogo = keurmerkUsps?.uspsMultiple.find(
    (usp) => usp.identity === 'online-aanbieder-medicijnen',
  )

  const leafletUrl = {
    label: 'Bijsluiter',
    value: product?.leaflet_url,
    attribute: 'leaflet_url',
  }

  const isLongDescription = product?.description?.html && product?.description?.html.length > 1500

  const [clamped, setClamped] = useState(isLongDescription)

  return (
    <ProductPageAccordionItem title='Gegevens' expanded>
      <ProductPageDescription
        right={[]}
        name={product?.name}
        description={product?.description}
        fontSize='body2'
        sx={[
          (theme) => ({
            '& h1': {
              typography: theme.typography.h5,
              fontWeight: theme.typography.fontWeightBold,
              mb: 1,
            },
            '& h2': {
              typography: theme.typography.subtitle1,
            },
            '& p': {
              mb: theme.spacings.xs,
            },
            '& p:last-of-type': {
              mb: 0,
            },
            '& table': {
              width: 'auto!important',
            },
            '& table tr > td': {
              p: '0 5px',
            },
            '& table tr > td:nth-of-type(1)': {
              pl: '0',
            },
            '& table tr > td:nth-last-of-type(1)': {
              pr: '0',
            },

            ...(clamped && {
              '& .ProductPageDescription-description': {
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 9,
              },
            }),
          }),
        ]}
      />

      {leafletUrl?.value && (
        <Link
          href={normalizeHref(leafletUrl.value)}
          target='_blank'
          title='Download bijsluiter'
          rel='noreferrer'
          sx={{ textDecoration: 'none' }}
        >
          <Button
            variant='cutLeft'
            size='small'
            color='primary'
            sx={(theme) => ({ mt: `${theme.spacings.xs}` })}
          >
            Download bijsluiter
          </Button>
        </Link>
      )}
      {isLongDescription && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            paddingBottom: '0 !important',
          }}
        >
          <Button variant='pill' onClick={() => setClamped((prevState) => !prevState)}>
            {clamped ? <Trans id='Read more' /> : <Trans id='Read less' />}
          </Button>
        </Box>
      )}

      {isMedical && (
        <Box
          sx={(theme) => ({
            '&.MuiBox-root': {
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              columnGap: theme.spacings.md,
              pb: 0,
            },
          })}
        >
          {keurmerkLogo?.asset && (
            <Box sx={(theme) => ({ p: `0 !important`, mt: theme.spacings.md })}>
              <Asset asset={keurmerkLogo.asset} quality={100} sx={{ width: 80, height: 'auto' }} />
            </Box>
          )}

          {OnlineMedicationProvidersLogo?.asset && (
            <Box sx={(theme) => ({ p: `0 !important`, mt: theme.spacings.md })}>
              <Link
                href='https://www.aanbiedersmedicijnen.nl/aanbieders/aanbiederslijst/nederlandse-drogisterij-service-b-v/index'
                target='_blank'
                title='Nederlandse Drogisterij Service B.V.'
              >
                <Asset
                  asset={OnlineMedicationProvidersLogo.asset}
                  quality={100}
                  sx={{ width: 80, height: 'auto' }}
                />
              </Link>
            </Box>
          )}
        </Box>
      )}
    </ProductPageAccordionItem>
  )
}
